import flatMap from "lodash/flatMap"
import { endOfDay } from "date-fns/endOfDay"
import { addWeeks, isSameDay, min } from "date-fns"
import startOfDay from "date-fns/startOfDay"
import { fromZonedTime } from "date-fns-tz"
import { isAfter } from "date-fns/isAfter"
import { getRefId, ensureNumber, isArchived } from "../utils/utils"
import { toZonedTime } from "date-fns-tz"
import { isTaskStarted } from "./started-work"
import addDays from "date-fns/addDays"
import addHours from "date-fns/addHours"
import { eachDayOfInterval } from "date-fns/eachDayOfInterval"
import { isBefore } from "date-fns/isBefore"
import subDays from "date-fns/subDays"
import differenceInBusinessDays from "date-fns/differenceInBusinessDays"
import isSameWeek from "date-fns/isSameWeek"

import differenceInWeeks from "date-fns/differenceInWeeks"
import subWeeks from "date-fns/subWeeks"
import { startOfWeek } from "date-fns/startOfWeek"
import { endOfWeek } from "date-fns/endOfWeek"

import subMonths from "date-fns/subMonths"
import addMonths from "date-fns/addMonths"
import { isWithinInterval } from "date-fns/isWithinInterval"

import getISODay from "date-fns/getISODay"
import eachWeekOfInterval from "date-fns/eachWeekOfInterval"
import areIntervalsOverlapping from "date-fns/areIntervalsOverlapping"
import differenceInDays from "date-fns/differenceInDays"
import differenceInCalendarDays from "date-fns/differenceInCalendarDays"

import { startOfMonth, endOfMonth } from "date-fns"
import { getBoardAction, getSections, toActionChange } from "./sections"

export function formatX(date, includeDay, shortMonth, numeric = false) {
    const options = {
        year: "numeric",
        // If numeric is true, we use "numeric", otherwise we respect the shortMonth toggle
        month: numeric ? "numeric" : shortMonth ? "short" : "long",
        day: "numeric",
    }

    if (includeDay) {
        options.weekday = "short"
    }

    return new Intl.DateTimeFormat(navigator.language, options).format(date)
}

export function utcToIsoString(date) {
    // 1. Handle null, undefined, or empty values early
    if (!date) {
        return null
    }

    try {
        // 2. Generate the date object
        const d = new Date(getTimeUtc(date))

        // 3. Check if the date is "Valid"
        // getTime() returns NaN for "Invalid Date" objects
        if (isNaN(d.getTime())) {
            console.warn("Invalid date provided to utcToIsoString:", date)
            return null
        }

        return d.toISOString()
    } catch (error) {
        // 4. Catch-all for any unexpected execution errors
        return null
    }
}

export function getNextWorkingDay(date) {
    const nextDay = new Date(date)
    nextDay.setDate(nextDay.getDate() + 1)
    nextDay.setHours(0, 0, 0, 0)
    // Skip weekends
    while (nextDay.getDay() === 6 || nextDay.getDay() === 0) {
        // 6 = Saturday, 0 = Sunday
        nextDay.setDate(nextDay.getDate() + 1)
    }
    return nextDay
}

export const formatDaysAndHoursFromHours = (h) => {
    const dv = Math.floor((h || 0) / 8)
    const days = h ? dv : null
    const hours = h ? h % 8 : null

    let st = ""

    if (days) {
        st = days + " day" + (days === 1 ? "" : "s")
    }
    if (hours) {
        st = st + " " + hours + " hour" + (hours === 1 ? "" : "s")
    }

    return st
}

export function isDateValid(date) {
    return date instanceof Date && !isNaN(date?.getTime())
}

export function dateValidationPass({ obj, startDate, endDate }) {
    if (!startDate && !endDate) return true
    let std = startDate || obj.startDate
    let end = endDate || obj.endDate
    if (std >= end) {
        return false
    } else {
        return true
    }
}

/**
 * Keeps a plan item's dates a valid range (the API refuses a start on or after the end). When
 * an edit would leave one, the date that wasn't edited moves and the item keeps its length;
 * with both edited, the end follows the start. Mutates and returns `change`.
 * @param item {object} The item as it is now
 * @param change {object}
 * @param [lengthFrom] {object} Whose length to keep - the item's by default
 * @return {object} change
 */
export function fitDateRange(item, change, lengthFrom = item) {
    if (!item || !change) return change

    const hasStart = change.startDate !== undefined && change.startDate !== null
    const hasEnd = change.endDate !== undefined && change.endDate !== null

    if (!hasStart && !hasEnd) return change

    const start = hasStart ? change.startDate : item.startDate
    const end = hasEnd ? change.endDate : item.endDate

    if (!start || !end || start < end) return change

    const days =
        lengthFrom?.startDate && lengthFrom?.endDate && lengthFrom.startDate < lengthFrom.endDate
            ? differenceInCalendarDays(toZonedTime(lengthFrom.endDate), toZonedTime(lengthFrom.startDate)) + 1
            : 1

    if (hasEnd && !hasStart) {
        const fitted = getTimeUtc(startOfDay(subDays(toZonedTime(end), days - 1)))

        // An end at midnight: the day before
        change.startDate = fitted < end ? fitted : getTimeUtc(startOfDay(subDays(toZonedTime(end), days)))
    } else {
        const fitted = getTimeUtc(endOfDay(addDays(toZonedTime(start), days - 1)))

        change.endDate = fitted > start ? fitted : getTimeUtc(endOfDay(addDays(toZonedTime(start), days)))
    }

    return change
}

/**
 * The dates to send again after the API refused a plan item's range. The date the change
 * didn't carry comes as it was on screen when that makes a range (the screen is what the
 * person meant), else moves to keep the item's length.
 * @param saved {object} The refusal's data: the dates the change would have left
 * @param change {object} The change that was refused
 * @param before {object} The item as it was on screen before the change
 * @return {object|null} {startDate, endDate}, null when there's nothing to retry
 */
export function getDatesRetry(saved, change, before) {
    if (!saved?.startDate || !saved?.endDate || !change) return null

    const hasStart = change.startDate !== undefined && change.startDate !== null
    const hasEnd = change.endDate !== undefined && change.endDate !== null

    // Both sent already fit (fitDateRange) - nothing to fill in
    if (hasStart === hasEnd) return null

    const asked = hasStart ? { startDate: change.startDate } : { endDate: change.endDate }
    const shown = hasStart ? { endDate: before?.endDate } : { startDate: before?.startDate }
    const retry = { ...shown, ...asked }

    if (retry.startDate && retry.endDate && retry.startDate < retry.endDate) return retry

    const fitted = fitDateRange(saved, { ...asked }, before)

    return fitted.startDate && fitted.endDate && fitted.startDate < fitted.endDate ? fitted : null
}

export function getStaleDate() {
    return getTimeUtc(subWeeks(new Date(), 7))
}
export function getCompleteBufferDate() {
    return getTimeUtc(subWeeks(new Date(), 1))
}

export function formatRateTimeUnits(unit) {
    switch (unit) {
        case "Monthly":
            return "p/m"

        case "Hourly":
            return "p/h"

        case "Daily":
            return "p/d"

        case "Weekly":
            return "p/w"

        case "One time":
            return "/1x"

        case "Yearly":
            return "p/y"

        default:
    }
}

export function getDateWithinMission({ date, mission }) {
    const start = toZonedTime(mission.planStartDate)
    const end = toZonedTime(mission.planEndDate)

    if (
        isWithinInterval(date, {
            start: start,
            end: end,
        }) ||
        isSameDay(date, start) ||
        isSameDay(date, end)
    ) {
        return date
    }

    if (isBefore(date, start)) {
        return start
    }
    if (isAfter(date, end)) {
        return end
    }

    return date
}

export function getTimeUtc(date) {
    try {
        const utcDate = fromZonedTime(date)
        return utcDate.getTime()
    } catch (err) {
        return undefined
    }
}

export function getStartHour(date) {
    return getISODay(date) * 8 - 8
}

export function getHoursNeeded(planItem) {
    if (!planItem) {
        return 0
    }
    if (planItem.hoursNeeded) {
        return planItem.hoursNeeded
    } else {
        return (differenceInBusinessDays(toZonedTime(planItem.endDate), toZonedTime(planItem.startDate)) + 1) * 8
    }
}
export function getMissionDateRange(mission) {
    let start = getSmallestStartDate(mission.planItems)
    let end = getGreatestEndDate(mission.planItems)

    if (!start || isBefore(toZonedTime(mission.planStartDate), start)) {
        start = toZonedTime(mission.planStartDate)
    }
    if (!end || isBefore(end, toZonedTime(mission.planEndDate))) {
        end = toZonedTime(mission.planEndDate)
    }

    return {
        start: start,
        end: end,
    }
}

/**
 * The span a project's money falls in: its plan dates, stretched to every plan item (lines,
 * roles, milestones), timesheet and expense outside them - the earliest and the latest of all.
 * Reads the lists as given (getMissionDateRange sorts mission.planItems in place).
 *
 * @return {{ start: Date|null, end: Date|null }} local dates, null when nothing has one
 */
export function getBudgetDates({ mission, timesheets = [], expenses = [] }) {
    let start = null
    let end = null
    const add = (d) => {
        if (!d) return
        const t = toZonedTime(d)
        if (Number.isNaN(t.getTime())) return
        if (!start || t < start) start = t
        if (!end || t > end) end = t
    }

    add(mission?.planStartDate)
    add(mission?.planEndDate)
    ;(mission?.planItems || []).forEach((p) => {
        if (!p || p.type === "phase") return
        add(p.startDate)
        add(p.endDate)
    })
    timesheets.forEach((t) => {
        add(t?.weekStart)
        add(t?.weekEnd)
    })
    expenses.forEach((e) => add(e?.expenseDate))

    return { start, end }
}

export function deriveStartDateAndTime(ai) {
    const start = typeof ai.startDate === "number" ? toZonedTime(ai.startDate) : ai.startDate

    const addr = ai.startHour ? ai.startHour % 8 : 0

    //

    return addHours(start, 9 + addr)
}

export function calculateDateDifference(startDate, endDate) {
    // Convert input strings to Date objects
    const start = new Date(startDate)
    const end = new Date(endDate)

    // Calculate total differences
    let years = end.getFullYear() - start.getFullYear()
    let months = end.getMonth() - start.getMonth()
    let days = end.getDate() - start.getDate()

    // Adjust for negative days
    if (days < 0) {
        months--
        days += new Date(end.getFullYear(), end.getMonth(), 0).getDate()
    }

    // Adjust for negative months
    if (months < 0) {
        years--
        months += 12
    }

    // Create result string
    const yearString = years > 0 ? `${years} year${years > 1 ? "s" : ""}` : ""
    const monthString = months > 0 ? `${months} month${months > 1 ? "s" : ""}` : ""
    const dayString = days > 0 ? `${days} day${days > 1 ? "s" : ""}` : ""

    // Concatenate parts with appropriate commas and "and"
    const parts = [yearString, monthString, dayString].filter((part) => part !== "")
    const result =
        parts.length > 1 ? parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1] : parts[0] || "0 days"

    return result
}

export function deriveEndDateAndTime(ai) {
    const start = typeof ai.startDate === "number" ? toZonedTime(ai.startDate) : ai.startDate

    if ((ai.startHour === null || ai.startHour === undefined) && ai.endDate) {
        return toZonedTime(ai.endDate)
    }

    if ((ai.startHour !== 0 && ai.startHour === null) || ai.startHour === undefined) {
        console.error("no startHour provided")
        return
    }

    const dur = ai.estimatedDuration || ai.duration

    if (!dur) {
        return null
    }

    let eod = dur / 8

    if (eod < 1) {
        eod = 1
    }

    let endDate

    if (ai.startHour % 8 === 0 && dur % 8 === 0) {
        endDate = endOfDay(addDays(start, eod - 1))
    } else {
        const myStartDate = startOfDay(deriveStartDateAndTime(ai))

        //Basic end date 00:00
        const howFarInAnEightHourDayAmIStarting = ai.startHour % 8

        const bleedToNextDay = (dur + howFarInAnEightHourDayAmIStarting) / 8

        const daysToAddToStartDate =
            (dur + howFarInAnEightHourDayAmIStarting) % 8 === 0 ? dur / 8 : Math.floor(bleedToNextDay)
        //End of day check

        const whatIsMyEndDateJustByEstimatedDuration = addDays(myStartDate, daysToAddToStartDate)

        let hoursToCover = 24

        try {
            hoursToCover = eachDayOfInterval({ start: start, end: whatIsMyEndDateJustByEstimatedDuration }).length * 8
        } catch (err) {}

        //What hours in the last day do I end?

        const leftOverHoursToDistribute = hoursToCover - dur
        const howManyGoToStart = howFarInAnEightHourDayAmIStarting
        const howManyGoToEnd = 8 - (leftOverHoursToDistribute - howManyGoToStart)

        const finalHoursToAdd = howManyGoToEnd + 9

        const myEndDateWithWorkDayTime =
            finalHoursToAdd === 17
                ? endOfDay(whatIsMyEndDateJustByEstimatedDuration)
                : addHours(whatIsMyEndDateJustByEstimatedDuration, finalHoursToAdd)

        endDate = myEndDateWithWorkDayTime
    }

    return endDate
}

export function findAiEndDate(ai) {
    if (ai.endDate) {
        return toZonedTime(ai.endDate)
    }

    if (ai.dueDate && (!ai.startDate || ai.startDate < ai.dueDate)) {
        return endOfDay(toZonedTime(ai.dueDate))
    }
    if (!ai.startDate && ai.completedOn) {
        return toZonedTime(ai.completedOn)
    }

    if (ai.week) {
        const startDay = addDays(toZonedTime(ai.week), Math.floor(ensureNumber(ai.startHour) / 8))
        const days = ai.estimatedDuration ? Math.ceil(ai.estimatedDuration / 8) - 1 : 1
        const end = endOfDay(addDays(startDay, days))

        return end
    }

    if (ai.startDate && ai.startHour >= 0 && ai.startHour !== null && ai.estimatedDuration > 0) {
        const days = ai.estimatedDuration / 8 - 1

        return endOfDay(addDays(toZonedTime(ai.startDate), days))
    }

    return null
}

export function getGreatestEndDate(list) {
    const sortedItems = list.sort((a, b) => a.endDate - b.endDate).reverse()

    if (sortedItems[0]?.endDate) {
        return endOfDay(toZonedTime(sortedItems[0].endDate))
    } else {
        return null
    }
}
export function getSmallestStartDate(list) {
    const sortedItems = list.sort((a, b) => a.startDate - b.startDate)

    if (sortedItems[0]?.startDate) {
        return startOfDay(toZonedTime(sortedItems[0].startDate))
    } else {
        return null
    }
}

export function getMyMissingTimesheets({ person, missions, myTimesheets, theStartDate }) {
    if (!theStartDate) theStartDate = startOfWeek(subMonths(new Date(), 6), { weekStartsOn: 1 })

    const myRoles = flatMap(
        missions.filter((m) => !m.noTimesheets),
        "planItems",
    )
        .filter((r) => r.type === "person")
        .filter((r) => {
            const foundRole = r.person === getRefId(person) || r.personLeftRole?.personId === getRefId(person)

            return foundRole
        })

    if (myRoles.length === 0) {
        return []
    }
    //hi
    const tsItems = myTimesheets.map((t) => ({ startDate: t.weekStart, endDate: t.weekEnd }))

    let planStart = subMonths(getSmallestStartDate(myRoles), 6)
    let planEnd = addMonths(getGreatestEndDate(myRoles), 6)

    const tsStart = subMonths(getSmallestStartDate(tsItems), 6)
    const tsEnd = addMonths(getGreatestEndDate(tsItems), 6)

    const smallestDate = isAfter(planStart, tsStart) ? tsStart : planStart
    const greatestEndDate = isAfter(tsEnd, planEnd) ? tsEnd : planEnd

    let missingMissionTimesheets = []

    //Roles first

    const roleStart = smallestDate
    const roleEnd = greatestEndDate

    const roleWeeks =
        !roleStart || isAfter(roleStart, new Date())
            ? []
            : eachWeekOfInterval(
                  {
                      start: roleStart,
                      end: new Date(),
                  },
                  {
                      weekStartsOn: 1,
                  },
              )

    roleWeeks.forEach((week, i) => {
        if (isAfter(week, new Date())) {
            return
        }
        let overLappingRoles = myRoles.filter((r) => {
            //What roles overlap any of the weeks from start to this week
            const std = toZonedTime(r.startDate)
            const end = toZonedTime(r.endDate)

            let isIn = false

            try {
                isIn = areIntervalsOverlapping(
                    {
                        start: std,
                        end: end,
                    },
                    {
                        start: week,
                        end: endOfWeek(week, { weekStartsOn: 1 }),
                    },
                )
            } catch (e) {}

            return isIn
        })

        overLappingRoles.forEach((role, i) => {
            const foundTimesheet = myTimesheets.find((tsr) => {
                return (
                    (tsr.snapshot?.planItemId || tsr.planItemId) === role._id &&
                    isSameWeek(week, toZonedTime(tsr.weekStart), { weekStartsOn: 1 })
                )
            })

            const isItMissing = !foundTimesheet

            if (isItMissing) {
                const mission = missions.find((m) => m._id === role.missionId)

                missingMissionTimesheets.push({
                    weekStart: getTimeUtc(week),
                    weekEnd: getTimeUtc(endOfWeek(week, { weekStartsOn: 1 })),
                    isMissing: true,
                    planItem: role,
                    mission: mission,
                    person: getRefId(person),
                    snapshot: {
                        planItem: role,
                        mission: mission,
                        person: person,
                    },
                })
            }
        })
    })

    return missingMissionTimesheets
}

export function getDisplayUnits(planItem) {
    if (planItem.durationNeededDisplayUnit === "Hours" || !planItem.durationNeededDisplayUnit) {
        return planItem.hoursNeeded
    } else if (planItem.durationNeededDisplayUnit === "Days") {
        return planItem.hoursNeeded / 8
    } else {
        return planItem.hoursNeeded / 8 / 5
    }
}

/**
 * The Timeline tab's "Change Project Dates" for a board project's end: a later
 * end is just more room, an earlier one compresses the timeline (phases are
 * cut to it, tasks planned after it go back to the planning board), which the
 * user confirms first unless the caller already has (skipConfirm: mAX's card).
 *
 * @param params {Object}
 * @param params.mission {Object}
 * @param params.app {Object}
 * @param params.date {Date} The new end (zoned)
 * @param [params.cb] {function(): void} Called once the end has landed
 * @param [params.skipConfirm] {Boolean} The user already confirmed a compression
 * @return {void}
 */
export function changeSimpleKanbanEndDate({ mission, app, date, cb, skipConfirm }) {
    const doChange = () => {
        const diffInDays = differenceInDays(date, toZonedTime(mission.planEndDate))

        if (diffInDays > 0) {
            app.missionUpdate(
                mission._id,
                {
                    planEndDate: getTimeUtc(endOfDay(date)),
                },
                () => {
                    if (cb) cb()
                },
            )
        } else {
            app.missionUpdate(
                mission._id,
                {
                    planEndDate: getTimeUtc(endOfDay(date)),
                },
                () => {
                    // Sections that run past the new end are pulled in (straight onto their actions, as
                    // the tags were saved: their tasks are handled below)
                    const sectionChanges = []

                    getSections(mission).forEach((st) => {
                        const std = toZonedTime(st.from)
                        const end = toZonedTime(st.to)
                        if (st.from && st.to && isAfter(end, endOfDay(date))) {
                            let newStart = st.from
                            let newEnd = st.to
                            const days =
                                eachDayOfInterval({
                                    start: endOfDay(date),
                                    end: end,
                                }).length - 1

                            newStart = subDays(std, days)
                            newEnd = endOfDay(date)

                            if (isAfter(newStart, newEnd)) {
                                newStart = startOfDay(date)
                            }

                            if (isAfter(toZonedTime(mission.planStartDate), newStart)) {
                                newStart = mission.planStartDate
                            }

                            sectionChanges.push({
                                _id: st._id,
                                ...toActionChange({ from: getTimeUtc(newStart), to: getTimeUtc(newEnd) }),
                            })
                        }
                    })

                    const afterSections = (done) =>
                        sectionChanges.length ? app.actionUpdateBulk(mission._id, sectionChanges, true, done) : done()

                    afterSections(
                        () => {
                            const aiChanges = mission.planItems
                                .flatMap((p) => p.actions)
                                .flatMap((a) => a.actionItems)
                                .map((ai) => {
                                    let changeNeeded = false
                                    let change = {
                                        _id: ai._id,
                                    }

                                    // A due date past the new end is pulled in to the end, not lost
                                    if (ai.endDate && isAfter(toZonedTime(ai.endDate), endOfDay(date))) {
                                        changeNeeded = true
                                        change.endDate = getTimeUtc(endOfDay(date))
                                    }
                                    if (ai.week && isAfter(toZonedTime(ai.week), endOfDay(date))) {
                                        changeNeeded = true
                                        change.week = null
                                        change.startDate = null
                                    }
                                    if (changeNeeded)
                                        return {
                                            obj: ai,
                                            change: change,
                                        }

                                    return null
                                })
                                .filter((c) => !!c)

                            if (aiChanges.length) {
                                app.actionItemUpdateBulk(mission._id, aiChanges)
                            }

                            if (cb) cb()
                        },
                    )
                },
            )
        }
    }
    const currentWeeks = eachWeekOfInterval(
        {
            start: toZonedTime(mission.planStartDate),
            end: toZonedTime(mission.planEndDate),
        },
        { weekStartsOn: 1 },
    ).length

    const newWeeks = eachWeekOfInterval(
        {
            start: toZonedTime(mission.planStartDate),
            end: endOfDay(date),
        },
        { weekStartsOn: 1 },
    ).length

    if (currentWeeks > newWeeks && !skipConfirm) {
        app.confirm({
            severe: true,
            comp: (
                <>
                    <p>
                        This cannot be undone and will compress your timeline and change the dates of any plan items
                        like tasks, roles, milestones that fall outside of the new date.
                    </p>
                    <p>
                        If any tasks are planned for after the new end date they will be sent back to your planning
                        board.
                    </p>
                </>
            ),
            yesText: "Make the changes",
            yesColor: "red",
            onYes: () => {
                doChange()
            },
        })
    } else {
        doChange()
    }
}

export function changeSimpleKanbanStartDate({ mission, app, date, shift, cb }) {
    const shiftWeeks = differenceInWeeks(date, toZonedTime(mission.planStartDate))

    const mainAction = getBoardAction(mission)

    const allActionItems = mission.planItems.flatMap((p) => p.actions).flatMap((a) => a.actionItems)

    app.block()

    let actionItemChangeArray = []

    if (!shift) {
        if (shiftWeeks === 0) {
            app.missionUpdate(
                mission._id,
                {
                    planStartDate: getTimeUtc(startOfDay(date)),
                },
                () => {
                    if (isAfter(date, toZonedTime(toZonedTime(mission.planStartDate)))) {
                        const newStartHour = getStartHour(date)

                        allActionItems.forEach((ai, i) => {
                            const sameWeekPass = isSameWeek(toZonedTime(ai.week), date, { weekStartsOn: 1 })

                            if (sameWeekPass && newStartHour > ai.startHour)
                                actionItemChangeArray.push({
                                    obj: ai,
                                    change: {
                                        _id: ai._id,
                                        startHour: newStartHour,
                                        estimatedDuration:
                                            newStartHour + ai.estimatedDuration > 56
                                                ? 56 - newStartHour
                                                : ai.estimatedDuration,
                                    },
                                })
                        })
                    }

                    if (mainAction) {
                        app.actionUpdateX(mainAction, { startDate: getTimeUtc(startOfDay(date)) })
                    }

                    if (actionItemChangeArray.length) {
                        setTimeout(() => {
                            app.actionItemUpdateBulk(mission._id, actionItemChangeArray)
                        }, 1000)
                    }

                    setTimeout(() => {
                        app.unBlock()
                        if (cb) cb()
                    }, 1000)
                },
            )
        } else if (shiftWeeks < 0) {
            app.missionUpdate(
                mission._id,
                {
                    planStartDate: getTimeUtc(startOfDay(date)),
                },
                () => {
                    app.unBlock()
                    if (cb) cb()
                },
            )
        } else if (shiftWeeks > 0) {
            app.missionUpdate(
                mission._id,
                {
                    planStartDate: getTimeUtc(startOfDay(date)),
                },
                () => {
                    allActionItems.forEach((ai, i) => {
                        const updateNeeded = isBefore(toZonedTime(ai.week), startOfWeek(date, { weekStartsOn: 1 }))

                        if (updateNeeded) {
                            const newWeek = startOfWeek(date, { weekStartsOn: 1 })
                            actionItemChangeArray.push({
                                obj: ai,
                                change: {
                                    _id: ai._id,
                                    week: getTimeUtc(newWeek),
                                },
                            })
                        }
                    })

                    if (mainAction) {
                        app.actionUpdateX(mainAction, { startDate: getTimeUtc(startOfDay(date)) })
                    }

                    if (actionItemChangeArray.length) {
                        setTimeout(() => {
                            app.actionItemUpdateBulk(mission._id, actionItemChangeArray)
                        }, 500)
                    }

                    setTimeout(() => {
                        app.unBlock()
                        if (cb) cb()
                    }, 1000)
                },
            )
        }
    } else {
        const shiftAmount = differenceInDays(date, toZonedTime(mission.planStartDate))

        const newEndDate = endOfDay(addDays(toZonedTime(mission.planEndDate), shiftAmount))

        app.missionUpdate(
            mission._id,
            {
                planStartDate: getTimeUtc(startOfDay(date)),
                planEndDate: getTimeUtc(newEndDate),
            },
            () => {
                // Everything that hasn't started moves as far as the project did, keeping its length:
                // its week on the workboard, its start (day and hour in that week) and its due date
                allActionItems.forEach((ai) => {
                    if (!ai || ai.status === "done" || isTaskStarted(ai, mission)) return
                    if (!ai.week && !ai.startDate && !ai.endDate) return

                    const change = { _id: ai._id }

                    if (ai.week) {
                        change.week = getTimeUtc(
                            startOfWeek(addDays(toZonedTime(ai.week), shiftAmount), { weekStartsOn: 1 }),
                        )
                    }
                    if (ai.startDate) {
                        const newStart = addDays(toZonedTime(ai.startDate), shiftAmount)

                        change.startDate = getTimeUtc(newStart)

                        if (typeof ai.startHour === "number") {
                            // Same hour of the day, on the new weekday
                            change.startHour = getStartHour(newStart) + (ai.startHour % 8)

                            if (typeof ai.estimatedDuration === "number" && change.startHour + ai.estimatedDuration > 56) {
                                change.estimatedDuration = 56 - change.startHour
                            }
                        }
                    }
                    if (ai.endDate) {
                        change.endDate = getTimeUtc(addDays(toZonedTime(ai.endDate), shiftAmount))
                    }

                    actionItemChangeArray.push({ obj: ai, change })
                })

                // The board's columns (sections) move the same distance, keeping their length
                const sectionChanges = getSections(mission)
                    .filter((st) => st.from && st.to)
                    .map((st) => ({
                        _id: st._id,
                        ...toActionChange({
                            from: getTimeUtc(addDays(toZonedTime(st.from), shiftAmount)),
                            to: getTimeUtc(addDays(toZonedTime(st.to), shiftAmount)),
                        }),
                    }))

                if (sectionChanges.length) {
                    app.actionUpdateBulk(mission._id, sectionChanges, true)
                }

                if (mainAction) {
                    app.actionUpdateX(mainAction, {
                        startDate: getTimeUtc(startOfDay(date)),
                        endDate: getTimeUtc(newEndDate),
                    })
                }

                if (actionItemChangeArray.length) {
                    setTimeout(() => {
                        app.actionItemUpdateBulk(mission._id, actionItemChangeArray)
                    }, 500)
                }

                // Milestones are days: they move the same days as everything else. Outcomes are
                // weeks: they move to the week that shift lands in
                const outcomeChanges = getMilestoneShiftChanges(mission, { days: shiftAmount, weekDays: shiftAmount })

                if (outcomeChanges.length) {
                    app.planItemUpdateBulk(mission._id, outcomeChanges)
                }

                setTimeout(() => {
                    app.unBlock()
                    if (cb) cb()
                }, 1000)
            },
        )
    }

    //Change planItem Date

    //change action date

    //change misison date

    //comit actionItems bulk
}

/**
 * A board's milestones and outcomes when its plan shifts.
 *
 * - A milestone is a day: it moves `days` days and keeps its length.
 * - An outcome is a week (filed on that week's Friday): it moves to the week its week lands in
 *   `weekDays` days later, the same way a task's week moves, on the same weekday.
 *
 * @param mission {Object}
 * @param params {{days: number, weekDays: number, after?: Date}} after: only the ones still to come
 * @return {Array<{obj: Object, change: Object}>} planItemUpdateBulk changes
 */
export function getMilestoneShiftChanges(mission, { days, weekDays, after }) {
    const changes = []
    const toCome = (p) => !after || isAfter(toZonedTime(p.startDate), after)

    ;(mission?.planItems || []).forEach((p) => {
        if (!p?.startDate || !toCome(p)) return

        if (p.type === "milestone" && days) {
            const start = addDays(toZonedTime(p.startDate), days)

            changes.push({
                obj: p,
                change: {
                    _id: p._id,
                    startDate: getTimeUtc(start),
                    endDate: getTimeUtc(p.endDate ? addDays(toZonedTime(p.endDate), days) : endOfDay(start)),
                },
            })
        } else if (p.type === "outcome" && weekDays) {
            const was = toZonedTime(p.startDate)
            const weekday = differenceInCalendarDays(was, startOfWeek(was, { weekStartsOn: 1 }))
            const week = startOfWeek(addDays(startOfWeek(was, { weekStartsOn: 1 }), weekDays), { weekStartsOn: 1 })
            const start = addDays(week, weekday)

            if (isSameDay(start, was)) return

            changes.push({
                obj: p,
                change: { _id: p._id, startDate: getTimeUtc(startOfDay(start)), endDate: getTimeUtc(endOfDay(start)) },
            })
        }
    })

    return changes
}

/**
 * "Shift what hasn't started" for a board project that is under way: the project keeps its
 * start and now ends on `date`. Tasks and milestones that haven't started move by as many
 * whole weeks as the end did, and so do the roles and costs that haven't started
 * (app.missionUpdate's `shift`). What is done or in progress stays where it is.
 */
export function shiftSimpleKanbanRemaining({ mission, app, date, cb }) {
    const oldEnd = mission.planEndDate
    const newEnd = getTimeUtc(endOfDay(date))
    const shiftWeeks = Math.round(differenceInDays(endOfDay(date), toZonedTime(oldEnd)) / 7)

    if (newEnd === oldEnd) return

    app.block()

    // The roles and costs that haven't started move with the end, in the same save as the
    // pinned ones (app.missionUpdatePinnedItems)
    const shift = { from: oldEnd, to: newEnd }

    app.missionUpdate(mission._id, { planEndDate: newEnd }, () => {
        const allActionItems = mission.planItems.flatMap((p) => p.actions).flatMap((a) => a.actionItems)
        const mainAction = getBoardAction(mission)

        const actionItemChanges = []
        const milestoneChanges = []

        if (shiftWeeks) {
            allActionItems.forEach((ai) => {
                if (!ai.week || isTaskStarted(ai, mission)) return

                actionItemChanges.push({
                    obj: ai,
                    change: {
                        _id: ai._id,
                        week: getTimeUtc(startOfWeek(addWeeks(toZonedTime(ai.week), shiftWeeks), { weekStartsOn: 1 })),
                        endDate: ai.endDate ? getTimeUtc(addWeeks(toZonedTime(ai.endDate), shiftWeeks)) : null,
                    },
                })
            })

        }

        // Milestones that haven't come yet move as many days as the end did; outcomes that
        // haven't come yet move as many weeks as the tasks did
        milestoneChanges.push(
            ...getMilestoneShiftChanges(mission, {
                days: differenceInCalendarDays(endOfDay(date), toZonedTime(oldEnd)),
                weekDays: shiftWeeks * 7,
                after: new Date(),
            }),
        )

        if (mainAction) {
            app.actionUpdateX(mainAction, { endDate: newEnd })
        }

        if (actionItemChanges.length) {
            setTimeout(() => {
                app.actionItemUpdateBulk(mission._id, actionItemChanges)
            }, 500)
        }

        if (milestoneChanges.length) {
            app.planItemUpdateBulk(mission._id, milestoneChanges)
        }

        setTimeout(() => {
            app.unBlock()
            if (cb) cb()
        }, 1000)
    }, undefined, { shift })
}

export function changeRoleDate({ planItem, change, app }) {}

export const isFullMonth = ({ start, end }) => {
    return (
        isSameDay(start, startOfMonth(start)) &&
        isSameDay(end, endOfMonth(start)) && // end must match the same month's end
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear()
    )
}

export const isFullWeek = ({ start, end }) => {
    return (
        isSameDay(start, startOfWeek(start, { weekStartsOn: 1 })) &&
        isSameDay(end, endOfWeek(start, { weekStartsOn: 1 })) &&
        isSameDay(startOfWeek(start, { weekStartsOn: 1 }), startOfWeek(end, { weekStartsOn: 1 }))
    )
}

export const getWeeklyCachedRanges = (cachedWeeks) => {
    if (cachedWeeks.length === 0) return []

    // Sort by the 'week' property
    const sorted = [...cachedWeeks].sort((a, b) => a.week - b.week)

    return sorted.reduce((ranges, current) => {
        const lastRangeObj = ranges[ranges.length - 1]

        if (!lastRangeObj) {
            // Initialize new range object
            ranges.push({
                range: [current.week, current.week],
                updatedAt: current.updatedAt,
            })
            return ranges
        }

        const lastWeekStart = lastRangeObj.range[1]
        const expectedNextWeek = addWeeks(lastWeekStart, 1)

        if (isSameDay(current.week, expectedNextWeek)) {
            // Extend range
            lastRangeObj.range[1] = current.week
            // Update the range's updatedAt to be the minimum of current and existing
            lastRangeObj.updatedAt = min([lastRangeObj.updatedAt, current.updatedAt])
        } else {
            // Start new range
            ranges.push({
                range: [current.week, current.week],
                updatedAt: current.updatedAt,
            })
        }

        return ranges
    }, [])
}
export const getWeeklyRanges = (weeks) => {
    if (weeks.length === 0) return []

    // Sort copies to prevent mutation
    const sortedWeeks = [...weeks].sort((a, b) => a - b)

    return sortedWeeks.reduce((ranges, currentWeek) => {
        const lastRange = ranges[ranges.length - 1]

        if (!lastRange) {
            ranges.push([currentWeek, currentWeek])
            return ranges
        }

        const lastWeekStart = lastRange[1]
        const expectedNextWeek = addWeeks(lastWeekStart, 1)

        if (isSameDay(currentWeek, expectedNextWeek)) {
            // Extend the existing range
            lastRange[1] = currentWeek
        } else {
            // Gap detected, start new range
            ranges.push([currentWeek, currentWeek])
        }

        return ranges
    }, [])
}

export const getArchivedDate = (person) => {
    if (isArchived(person)) {
        const today = startOfDay(new Date())
        if (person.endDate && isBefore(toZonedTime(person.endDate), today)) {
            return toZonedTime(person.endDate)
        }
        if (person.endDate && isAfter(toZonedTime(person.endDate), today)) {
            return today
        }
        if (person.endDate && isSameDay(toZonedTime(person.endDate), today)) {
            return today
        }

        return toZonedTime(person.updatedAt)
    }

    return null
}

// Whether a person belonged to the org at some point in `dates` ({ start, end }),
// judged by their start and end dates. An archived person with no end date is
// taken to have left when they were archived (updatedAt).
export const isPersonInDateRange = (p, dates) => {
    if (isArchived(p)) {
        if (!p.endDate && p.startDate) {
            return areIntervalsOverlapping(
                {
                    start: toZonedTime(p.startDate),
                    end: new Date(p.updatedAt),
                },
                dates,
            )
        } else if (!p.endDate && !p.startDate) {
            return (
                isAfter(new Date(p.updatedAt), dates.end) ||
                isSameDay(new Date(p.updatedAt), dates.end) ||
                isWithinInterval(new Date(p.updatedAt), dates)
            )
        }
    }

    if (p.startDate && p.endDate) {
        return areIntervalsOverlapping(
            {
                start: toZonedTime(p.startDate),
                end: toZonedTime(p.endDate),
            },
            dates,
        )
    } else if (p.startDate && !p.endDate) {
        return (
            isBefore(toZonedTime(p.startDate), dates.start) ||
            isSameDay(toZonedTime(p.startDate), dates.start) ||
            isWithinInterval(toZonedTime(p.startDate), dates)
        )
    } else if (!p.startDate && p.endDate) {
        return (
            isAfter(toZonedTime(p.endDate), dates.end) ||
            isSameDay(toZonedTime(p.endDate), dates.end) ||
            isWithinInterval(toZonedTime(p.endDate), dates)
        )
    }

    return true
}
